import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Candidates from './Candidates';
import Settings from './Settings';
import Contact from '../contact/Contact';
import LogoutIcon from '@mui/icons-material/Logout';
import { FaCog, FaUsers, FaRegEnvelope, FaQuestionCircle, FaHome, FaArrowLeft } from 'react-icons/fa'; // Import FaArrowLeft
import './userdashboard.css';

const UserDashboard = () => {
  const [activeTab, setActiveTab] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [candidateView, setCandidateView] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const getGreeting = () => {
    const hour = new Date().getHours();
    const name = user?.userName || 'User';
    if (hour < 12) return `Good morning, ${name}`;
    if (hour < 18) return `Good afternoon, ${name}`;
    return `Good evening, ${name}`;
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin');
    }
    window.scrollTo(0, 0);
  }, [user, navigate]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setShowHelp(false);
    setCandidateView(false);
    window.scrollTo(0, 0);
  };

  const handleCandidateSelect = (selected) => {
    setCandidateView(selected);
    setShowHelp(false);
  };

  const toggleHelp = () => setShowHelp(!showHelp);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleBackToDashboard = () => {
    setActiveTab(''); // Set activeTab to empty string to show default dashboard content
    setShowHelp(false);
    setCandidateView(false);
    window.scrollTo(0, 0);
  };

  const renderContent = () => {
    const showBackButton = activeTab !== '';

    let currentContentComponent;
    if (activeTab === 'candidates') {
      currentContentComponent = <Candidates onCandidateSelect={handleCandidateSelect} />;
    } else if (activeTab === 'settings') {
      currentContentComponent = <Settings />;
    } else if (activeTab === 'contactUs') {
      currentContentComponent = <Contact />;
    } else { // Default dashboard view
      currentContentComponent = (
        <div className="dashboard-main">
          <div className="welcome-section">
            <p>Get started by selecting an option below or from the sidebar</p>
          </div>
          
          <div className="card-grid">
            <div className="dashboard-card" onClick={() => handleTabClick('candidates')}>
              <div className="card-icon-container">
                <FaUsers className="card-icon" />
              </div>
              <h3>Candidates</h3>
              <p>Manage all candidates and video submissions</p>
              <div className="card-hover-content">
                <span>View Candidates →</span>
              </div>
            </div>
            
            <div className="dashboard-card" onClick={() => handleTabClick('settings')}>
              <div className="card-icon-container">
                <FaCog className="card-icon" />
              </div>
              <h3>Settings</h3>
              <p>Update account and organization details</p>
              <div className="card-hover-content">
                <span>Go to Settings →</span>
              </div>
            </div>
            
            <div className="dashboard-card" onClick={() => handleTabClick('contactUs')}>
              <div className="card-icon-container">
                <FaRegEnvelope className="card-icon" />
              </div>
              <h3>Support</h3>
              <p>Contact our assistance team</p>
              <div className="card-hover-content">
                <span>Get Support →</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Render the specific content component first */}
        {currentContentComponent}

        {/* Render "Back to Dashboard" button at the bottom if not on the main dashboard */}
        {showBackButton && (
          <div className="back-to-dashboard-container">
            <button className="back-to-dashboard-button" onClick={handleBackToDashboard}>
              <FaArrowLeft className="button-icon" /> Back to Dashboard
            </button>
          </div>
        )}
      </>
    );
  };


  const renderRightPanel = () => {
    return (
      <div className="right-panel">
        <div className="panel-section quick-actions">
          <h3>Quick Actions</h3>
          <ul>
            <li onClick={() => handleTabClick('candidates')}>
              <FaUsers /> View Candidates
            </li>
            <li onClick={() => handleTabClick('settings')}>
              <FaCog /> Update Profile
            </li>
          </ul>
        </div>
      </div>
    );
  };

  const getHelpContent = () => {
    if (activeTab === '') {
      return (
        <div className="help-content">
          <h3>Candidate Management Guide</h3>
          <p>To access candidate information:</p>
          <ul>
            <li><strong>Candidates</strong> - Click on candidates to view all candidates you've requested video submissions from</li>
            <li><strong>Candidate List</strong> - Displays candidate names, email addresses, and available actions</li>
            <li><strong>Name Field</strong> - Select any candidate name to access their video submissions and status details</li>
            <li><strong>Action Button</strong> – The "Request References" option becomes available once a candidate has completed their pre-screening video. When clicked, this button initiates a recorded reference interview with the candidate's previous employer, conducted by First Take Interviews Inc.</li>
          </ul>
        </div>
      );
    }

    if (activeTab === 'candidates' && !candidateView) {
      return (
        <div className="help-content">
          <h3>Candidate Management Guide</h3>
          <ul>
            <li><strong>Candidate List</strong> - Displays candidate names, email addresses, and available actions</li>
            <li><strong>Name Field</strong> - Select any candidate name to access their video submissions and status details</li>
            <li><strong>Action Button</strong> – The "Request References" option becomes available once a candidate has completed their pre-screening video. When clicked, this button initiates a recorded reference interview with the candidate's previous employer, conducted by First Take Interviews Inc.</li>
          </ul>
        </div>
      );
    }

    if (activeTab === 'candidates' && candidateView) {
      return (
        <div className="help-content">
          <h3>Candidate Management</h3>
          <p>Key features in this section:</p>
          <ul>
            <li><strong>Video Submissions:</strong>
              <ul>
                <li><em>Reference Video</em> - Verified professional references from previous employers</li>
                <li><em>Pre-screening Video</em> - Initial candidate interviews for your review</li>
              </ul>
            </li>
            <li><strong>Previous Employer</strong> - Displays the organization where the candidate was most recently employed</li>
            <li><strong>Title at Employer</strong> - Indicates the candidate's position at their previous organization</li>
            <li><strong>Status Indicators</strong> - Shows the current state of each video submission request</li>
            <li><strong>Video Playback</strong> - Select the play button to view available video submissions</li>
          </ul>
        </div>
      );
    }

    if (activeTab === 'settings') {
      return (
        <div className="help-content">
          <h3>Account Settings</h3>
          <p>Available configuration options:</p>
          <ul>
            <li>Update your organization's contact information</li>
            <li>Change your account password</li>
            <li>Manage notification preferences</li>
          </ul>
        </div>
      );
    }

    if (activeTab === 'contactUs') {
      return (
        <div className="help-content">
          <h3>Support Center</h3>
          <p>Available support options:</p>
          <ul>
            <li>Direct messaging to our support team</li>
            <li>Access to our contact information</li>
            <li>Technical assistance and troubleshooting</li>
          </ul>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar Navigation */}
      <div className="dashboard-sidebar">
        <div className="sidebar-header">
          
        </div>
        
        <nav className="sidebar-nav">
          <ul>
            <li 
              className={activeTab === '' ? 'active' : ''}
              onClick={() => handleTabClick('')}
            >
              <FaHome className="nav-icon" />
              <span>Dashboard</span>
            </li>
            <li 
              className={activeTab === 'candidates' ? 'active' : ''}
              onClick={() => handleTabClick('candidates')}
            >
              <FaUsers className="nav-icon" />
              <span>Candidates</span>
            </li>
            <li 
              className={activeTab === 'settings' ? 'active' : ''}
              onClick={() => handleTabClick('settings')}
            >
              <FaCog className="nav-icon" />
              <span>Settings</span>
            </li>
            <li 
              className={activeTab === 'contactUs' ? 'active' : ''}
              onClick={() => handleTabClick('contactUs')}
            >
              <FaRegEnvelope className="nav-icon" />
              <span>Support</span>
            </li>
            <li 
        className="logout-button"
        onClick={handleLogout}
      >
        <LogoutIcon fontSize="small" className="nav-icon" />
        <span>Logout</span>
      </li>
          </ul>
        </nav>
        </div>
        
      {/* Main Content Area */}
      <div className="dashboard-content">
        <header className="dashboard-header">
          <div className="greeting-section">
            <h6>{getGreeting()}</h6>
            <p>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
          
          <div className="help-icon" onClick={toggleHelp}>
            <FaQuestionCircle />
            <span>Help</span>
          </div>
        </header>
        
        <div className="content-area">
          <div className="main-content">
            {renderContent()}
          </div>
          
          {/* Right Panel - Always shows quick actions */}
          {renderRightPanel()}
          
          {/* Help Panel - Only shows when help icon is clicked */}
          {showHelp && (
            <div className="help-modal">
              <div className="help-modal-content">
                <div className="help-modal-header">
                  <h3>Help Center</h3>
                  <button className="close-help" onClick={toggleHelp}>×</button>
                </div>
                <div className="help-modal-body">
                  {getHelpContent()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;